import { DEVICE_PULL_UP_TIPS_MAP, DEVICE_PULL_UP_TYPE, INSTANCE as adapter } from '@/netadapters/CloudAdapter'
import type { DeviceConfigCheckRequestData } from '@/netadapters/CloudAdapter'
import { INSTANCE as DeviceManager } from '../devices/DeviceManager'
import { INSTANCE as GuideManager } from '@/core/GuideManager'
import { ConnectionState } from '../devices/IDevice'
import { INSTANCE as ToastManager } from '@/core/ToastManager'

const FLAG_INIT = 0b0000000
const FLAG_ON_GOING = 0b0100000
const FLAG_TERMINATE = 0b1000000
const FLAG_ERROR = FLAG_TERMINATE | 0b010000

export enum PullUpDeviceState {
  INIT = FLAG_INIT,
  ON_CHECK_CONFIG = FLAG_ON_GOING,
  ON_START_PULL_UP_DEVICE = FLAG_ON_GOING | 0b1,
  ON_STARTED_PULL_UP_DEVICE = FLAG_ON_GOING | 0b10,
  ERR_NOT_IN_CONNECT_TIME = FLAG_ERROR | 0b1,
  ERR_NOT_FIND_DEVICE = FLAG_ERROR | 0b10,
  ERR_OWORK_DISABLED = FLAG_ERROR | 0b11,
  ERR_PWD_INPUT_NEEDED = FLAG_ERROR | 0b100,
  ERR_PULL_TIMEOUT = FLAG_ERROR | 0b101,
  ERR_UNSET_PASSWORD = FLAG_ERROR | 0b110,
  ERR_COMMON = FLAG_ERROR | 0b111,
  SUCCESS = FLAG_TERMINATE,
}

export enum PullUpDeviceType {
  BY_MANUAL_CONFIRM,
  BY_PASSWORD,
  IN_CONNECT_TIME,
}

export const PullUpDeviceTypeStringMap = new Map<PullUpDeviceType, string>([
  [PullUpDeviceType.BY_MANUAL_CONFIRM, 'manualConfirmation'],
  [PullUpDeviceType.BY_PASSWORD, 'passwordConfirmation'],
  [PullUpDeviceType.IN_CONNECT_TIME, 'inConnectTimeConfirmation'],
])

export interface PullUpDeviceParams {
  deviceId: string
  pushPassword?: string
  type?: PullUpDeviceType
}

export const DEVICE_STATUS_CHANGE_TIMEOUT_IN_MS = 10 * 1000

export const MANUAL_CONFIRMATION_TIMEOUT_IN_SEC = 60

export const MANUAL_CONFIRMATION_TIMEOUT_IN_MS = MANUAL_CONFIRMATION_TIMEOUT_IN_SEC * 1000

export interface PullUpDeviceListener {
  // TODO:js code
  onPullSuccess(deviceId: string): void
  onPullTimeout(deviceId: string): void
  onPullFailed(deviceId: string, state: PullUpDeviceState, errorCode?: DEVICE_PULL_UP_TYPE): void
  onPullingStart?(deviceId: string, state: PullUpDeviceState): void
  onPullDeviceStateChange?(deviceId: string, state: PullUpDeviceState): void
}

export class DevicePullUpTask {
  #listener: (PullUpDeviceListener | undefined)[] = []
  #state: PullUpDeviceState = PullUpDeviceState.INIT
  #timer: ReturnType<typeof setTimeout> | null = null
  #waitTime: number
  #deviceId: string
  #pullUpDeviceType?: PullUpDeviceType
  #isShowConnectModal = false

  constructor(deviceId: string, waitTime = DEVICE_STATUS_CHANGE_TIMEOUT_IN_MS, type?: PullUpDeviceType) {
    this.#waitTime = waitTime
    this.#deviceId = deviceId
    this.#pullUpDeviceType = type
  }

  reset() {
    this.#clearTimeout()
    this.#listener = []
    this.#setPullState(PullUpDeviceState.INIT)
  }

  get type() {
    return this.#pullUpDeviceType
  }

  #clearTimeout() {
    if (this.#timer) {
      clearTimeout(this.#timer)
      this.#timer = null
    }
  }

  #showConnectGuide() {
    GuideManager.showConnectGuide(this.#deviceId, this.#state)
  }

  async #checkDeviceState(): Promise<DeviceConfigCheckRequestData | undefined> {
    if (this.#state === PullUpDeviceState.ON_CHECK_CONFIG) {
      return
    }
    this.#setPullState(PullUpDeviceState.ON_CHECK_CONFIG)

    try {
      const result = await adapter.deviceConfigCheck(this.#deviceId)

      if (result?.data) {
        if (!result.data.inConnectTime) {
          this.#setPullState(PullUpDeviceState.ERR_NOT_IN_CONNECT_TIME)
        }

        if (!result.data.openOworkSwitch) {
          this.#setPullState(PullUpDeviceState.ERR_OWORK_DISABLED)
        }

        return result?.data
      }

      return undefined
    } catch (error) {
      return undefined
    }
  }

  async #pullUpDeviceImpl(isShowTips: boolean, type: PullUpDeviceType, pushPassword?: string) {
    if (this.#state === PullUpDeviceState.ON_START_PULL_UP_DEVICE) {
      return
    }
    this.#setPullState(PullUpDeviceState.ON_START_PULL_UP_DEVICE)

    try {
      const result = await adapter.pullUpDevice(this.#deviceId, this.#getPullUpDeviceType(type), pushPassword)

      switch (result?.data) {
        case DEVICE_PULL_UP_TYPE.SUCCESS:
          this.#waiteDeviceStateChange(isShowTips)
          this.#setPullState(PullUpDeviceState.ON_STARTED_PULL_UP_DEVICE)

          return
        case DEVICE_PULL_UP_TYPE.WRONG_PASSWORD:
          this.#setPullState(PullUpDeviceState.ERR_PWD_INPUT_NEEDED, result.data)
          break
        case DEVICE_PULL_UP_TYPE.UNOPENED_OWORK:
          this.#setPullState(PullUpDeviceState.ERR_OWORK_DISABLED, result.data)
          break

        case DEVICE_PULL_UP_TYPE.UNSET_PASSWORD:
          this.#setPullState(PullUpDeviceState.ERR_UNSET_PASSWORD, result.data)
          break
        case DEVICE_PULL_UP_TYPE.NO_DEVICE:
          this.#setPullState(PullUpDeviceState.ERR_NOT_FIND_DEVICE, result.data)
          break
        case DEVICE_PULL_UP_TYPE.CONNECTED_TIMEOUT:
          this.#setPullState(PullUpDeviceState.ERR_PULL_TIMEOUT, result.data)
          break
        default:
          this.#setPullState(PullUpDeviceState.ERR_COMMON, result?.data)
          break
      }

      if (isShowTips) {
        const tips =
          DEVICE_PULL_UP_TIPS_MAP.get(result?.data || DEVICE_PULL_UP_TYPE.NORMAL_ERROR) ??
          DEVICE_PULL_UP_TIPS_MAP.get(DEVICE_PULL_UP_TYPE.NORMAL_ERROR)

        this.#showToast(tips)
      }
    } catch {
      this.#setPullState(PullUpDeviceState.ERR_COMMON, DEVICE_PULL_UP_TYPE.NORMAL_ERROR)
    }
  }

  #showToast(tips: string | undefined) {
    if (tips) {
      ToastManager.show(tips)
    } else {
      console.log('tips get error')
    }
  }

  #setPullState(state: PullUpDeviceState, errorCode?: DEVICE_PULL_UP_TYPE) {
    this.#state = state

    switch (state) {
      case PullUpDeviceState.SUCCESS:
        this.#onSuccess()
        break
      case PullUpDeviceState.ERR_PULL_TIMEOUT:
        this.#onTimeout()
        break
      case PullUpDeviceState.ERR_OWORK_DISABLED:
        if (this.#isShowConnectModal) {
          this.#showConnectGuide()
        }
        break
      case PullUpDeviceState.ERR_PWD_INPUT_NEEDED:
      case PullUpDeviceState.ERR_NOT_FIND_DEVICE:
      case PullUpDeviceState.ERR_NOT_IN_CONNECT_TIME:
      case PullUpDeviceState.ERR_COMMON:
      case PullUpDeviceState.ERR_UNSET_PASSWORD:
        this.#onFailed(errorCode)

        break
      default:
        break
    }

    this.#onPullDeviceStateChange()
  }

  #waiteDeviceStateChange(isShowTips: boolean) {
    const listener = {
      onDeviceListUpdated: () => {
        this.#isDeviceConnected()
      },
      onCurrentSelectedDeviceChanged: () => {
        // do nothing
      },
      onInitialized(): void {
        // do nothing
      },
    }

    DeviceManager.addChangeListener(listener)

    this.#timer = setTimeout(() => {
      const tips = DEVICE_PULL_UP_TIPS_MAP.get(DEVICE_PULL_UP_TYPE.CONNECTED_TIMEOUT)

      if (isShowTips) this.#showToast(tips)
      this.#setPullState(PullUpDeviceState.ERR_PULL_TIMEOUT)

      DeviceManager.removeChangeListener(listener)
    }, this.#waitTime)
  }

  #onPullingStart() {
    console.log(`on Pull State Change:${this.#deviceId}`, this.#state)

    for (const listener of this.#listener) {
      try {
        //listeners 是外部注册的 可能页面销毁了  listener被回收 可能会报错
        listener?.onPullingStart?.(this.#deviceId, this.#state)
      } catch (error) {
        console.error(error)
      }
    }
  }

  #onPullDeviceStateChange() {
    console.log(`on Pull State Change:${this.#deviceId}`, this.#state)

    for (const listener of this.#listener) {
      try {
        listener?.onPullDeviceStateChange?.(this.#deviceId, this.#state)
      } catch (error) {
        console.error(error)
      }
    }
  }

  #onTimeout() {
    console.log(`on Pull #onTimeout:${this.#deviceId}`)

    for (const listener of this.#listener) {
      try {
        listener?.onPullTimeout(this.#deviceId)
      } catch (error) {
        console.error(error)
      }
    }

    this.reset()
  }

  #onSuccess() {
    console.log(`on Pull #onSuccess:${this.#deviceId}`)

    for (const listener of this.#listener) {
      try {
        listener?.onPullSuccess(this.#deviceId)
      } catch (error) {
        console.error(error)
      }
    }
    this.reset()
  }

  #onFailed(errorCode?: DEVICE_PULL_UP_TYPE) {
    console.log(`on Pull error:${this.#deviceId} ${errorCode}`)

    for (const listener of this.#listener) {
      try {
        listener?.onPullFailed(this.#deviceId, this.#state, errorCode)
      } catch (error) {
        console.error(error, errorCode)
      }
    }
    this.reset()
  }

  #isDeviceConnected() {
    const device = DeviceManager.getDeviceById(this.#deviceId)

    if (device?.connectionState === ConnectionState.CONNECTED) {
      this.#setPullState(PullUpDeviceState.SUCCESS)

      this.#clearTimeout()

      return true
    }

    return false
  }

  addListener(listener: PullUpDeviceListener) {
    if ([PullUpDeviceState.ON_CHECK_CONFIG, PullUpDeviceState.ON_START_PULL_UP_DEVICE].includes(this.#state)) {
      this.#onPullingStart()
    }
    this.#listener.push(listener)
  }

  async start(
    params: PullUpDeviceParams,
    listener?: PullUpDeviceListener,
    isShowTips = false,
    isShowConnectModal = false,
    isCheckConfig = true
  ) {
    if (this.#state === PullUpDeviceState.ON_CHECK_CONFIG) return
    this.#isShowConnectModal = isShowConnectModal
    if (listener) this.addListener(listener)
    const isConnected = this.#isDeviceConnected()

    if (isConnected) return

    if (!isCheckConfig && this.#pullUpDeviceType !== undefined) {
      this.#pullUpDeviceImpl(isShowTips, this.#pullUpDeviceType, params.pushPassword)

      return
    }
    const data = await this.#checkDeviceState()

    if (data?.inConnectTime && data?.openOworkSwitch) {
      this.#pullUpDeviceType = PullUpDeviceType.IN_CONNECT_TIME
      this.#pullUpDeviceImpl(isShowTips, this.#pullUpDeviceType, params.pushPassword)
    } else if (isShowConnectModal) {
      this.#showConnectGuide()
    }
  }

  #getPullUpDeviceType(type: PullUpDeviceType) {
    return PullUpDeviceTypeStringMap.get(type)!
  }
}
